import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Temporary local-only password gate.
 * - First visit: user chooses a password (stored hashed in localStorage on this device).
 * - Later visits: user enters that same password to unlock.
 * No email, no account, no server. Keeps the existing Supabase auth wired up
 * behind the AUTH_ENABLED flag for future use.
 */

const HASH_KEY = "hockey_local_pw_hash";
const UNLOCK_KEY = "hockey_local_pw_unlocked";

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function LocalPasswordGate() {
  const navigate = useNavigate();
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHasPassword(!!localStorage.getItem(HASH_KEY));
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 4) return setError("Password must be at least 4 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setIsLoading(true);
    try {
      const hash = await sha256(password);
      localStorage.setItem(HASH_KEY, hash);
      sessionStorage.setItem(UNLOCK_KEY, "1");
      toast.success("Password set — you're in.");
      navigate("/", { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const hash = await sha256(password);
      if (hash === localStorage.getItem(HASH_KEY)) {
        sessionStorage.setItem(UNLOCK_KEY, "1");
        toast.success("Welcome back!");
        navigate("/", { replace: true });
      } else {
        setError("Incorrect password");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (!confirm) {
      // No-op: reuse confirm field state irrelevantly; use window.confirm instead.
    }
    if (window.confirm("Reset password? You'll be asked to choose a new one.")) {
      localStorage.removeItem(HASH_KEY);
      sessionStorage.removeItem(UNLOCK_KEY);
      setHasPassword(false);
      setPassword("");
      setConfirm("");
      setError(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 pt-16">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">🏒 IceHub</CardTitle>
          <CardDescription>
            {hasPassword ? "Enter your password to continue" : "Choose a password to access IceHub"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={hasPassword ? handleUnlock : handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="local-password">Password</Label>
              <Input
                id="local-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
            </div>
            {!hasPassword && (
              <div className="space-y-2">
                <Label htmlFor="local-confirm">Confirm password</Label>
                <Input
                  id="local-confirm"
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {hasPassword ? "Unlock" : "Set password & continue"}
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Button variant="link" className="px-0" onClick={() => navigate("/")}>
              ← Back to Home
            </Button>
            {hasPassword && (
              <Button variant="link" className="px-0 text-muted-foreground" onClick={handleReset}>
                Forgot / reset
              </Button>
            )}
          </div>

          <p className="mt-4 text-xs text-muted-foreground text-center">
            Temporary local sign-in. Your password is stored only on this device.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
